import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";

/**
 * Super-admin case management page. This is the primary human-review surface
 * for report handling — the AI moderation assistant triages reports into
 * cases, and admins issue the final enforcement decision here.
 *
 * Layout: left-hand queue (urgent first), right-hand detail with AI summary,
 * evidence, business snapshot, and the admin decision panel.
 *
 * Permission-ready: only super_admin can reach this page for now, but the
 * shape assumes future roles (moderator, support, finance) will each see a
 * different subset of dashboard sections — see AdminDashboard.jsx sidebar.
 */
const DECISIONS = [
  { value: "dismiss", label: "Dismiss (no action)", color: "#4b5563" },
  { value: "warning", label: "Send warning", color: "#d97706" },
  { value: "strike", label: "Issue strike (apply ladder)", color: "#dc2626" },
  { value: "suspension", label: "Suspend directly", color: "#dc2626" },
  { value: "removal", label: "Permanent removal", color: "#7f1d1d" },
];

const PRIORITY_BADGE = {
  urgent: { label: "Urgent", bg: "#dc2626", fg: "#fff" },
  high: { label: "High", bg: "#d97706", fg: "#fff" },
  normal: { label: "Normal", bg: "var(--gf-surface-2)", fg: "var(--gf-text-muted)" },
};

const STATUS_LABELS = {
  open: "Open",
  waiting_customer: "Waiting on customer",
  waiting_business: "Waiting on business",
  under_review: "Under review",
  resolved: "Resolved",
  closed: "Closed",
};

export default function AdminCases() {
  const { user, authHeader } = useAuth();
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all"); // all|urgent|open
  const [loading, setLoading] = useState(true);

  const loadCases = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "urgent") params.priority = "urgent";
      if (filter === "open") params.status = "open";
      const r = await axios.get(`${API}/admin/cases`, { headers: authHeader(), params });
      setCases(r.data.cases || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load cases"); }
    finally { setLoading(false); }
  };

  const openCase = async (c) => {
    setSelected(c.id);
    setDetail(null);
    try {
      const r = await axios.get(`${API}/admin/cases/${c.id}`, { headers: authHeader() });
      setDetail(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load case"); }
  };

  useEffect(() => { loadCases(); /* eslint-disable-next-line */ }, [filter]);

  if (!user || user.role !== "super_admin") {
    // Defence-in-depth 403 — normally unreachable because <RequireAuth
    // roles={["super_admin"]}> in App.js intercepts non-admins first. If it
    // ever does render, use neutral wording (no internal role names leak).
    return (
      <div className="min-h-screen" style={{ background: "var(--gf-bg)" }}>
        <Header />
        <div className="max-w-3xl mx-auto p-8">
          <h1 className="font-display text-3xl" style={{ color: "var(--gf-text)" }}>Access restricted</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--gf-text-muted)" }}>
            You don&rsquo;t have permission to view this page.
          </p>
          <Link to="/" className="mt-4 inline-block underline text-sm" style={{ color: "var(--gf-accent)" }}>Back home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--gf-bg)" }}>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-cases-page">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>
              Trust &amp; safety
            </div>
            <h1 className="font-display text-4xl mt-1" style={{ color: "var(--gf-text)" }}>Case management</h1>
          </div>
          <div className="flex gap-1" role="tablist">
            {["all","urgent","open"].map((f) => (
              <button
                key={f}
                data-testid={`case-filter-${f}`}
                onClick={() => setFilter(f)}
                className="rounded-full px-3 py-1.5 text-xs"
                style={{
                  background: filter === f ? "var(--gf-accent)" : "var(--gf-surface-2)",
                  color: filter === f ? "var(--gf-on-accent)" : "var(--gf-text-muted)",
                }}>
                {f === "all" ? "All" : f === "urgent" ? "Urgent only" : "Open only"}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-[380px,1fr] gap-6 mt-6">
          {/* Queue */}
          <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="case-queue">
            <div className="p-4 border-b text-xs uppercase tracking-[0.2em]" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>
              {loading ? "Loading…" : `${cases.length} case${cases.length === 1 ? "" : "s"}`}
            </div>
            <div className="max-h-[70vh] overflow-y-auto divide-y" style={{ borderColor: "var(--gf-border)" }}>
              {cases.length === 0 && !loading && (
                <div className="p-6 text-sm text-center" style={{ color: "var(--gf-text-muted)" }}>
                  No cases — enjoy the calm.
                </div>
              )}
              {cases.map((c) => {
                const b = PRIORITY_BADGE[c.priority] || PRIORITY_BADGE.normal;
                return (
                  <button
                    key={c.id}
                    data-testid={`case-row-${c.id}`}
                    onClick={() => openCase(c)}
                    className="w-full text-left p-4 hover:bg-black/5 transition-colors"
                    style={{ background: selected === c.id ? "var(--gf-surface-2)" : "transparent" }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-[0.2em] rounded-full px-2 py-0.5"
                        style={{ background: b.bg, color: b.fg }}>
                        {b.label}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
                        {STATUS_LABELS[c.status] || c.status}
                      </span>
                      {c.final_decision && (
                        <span className="text-[10px] rounded-full px-2 py-0.5" style={{ background: "var(--gf-surface-2)", color: "var(--gf-text-muted)" }}>
                          {c.final_decision}
                        </span>
                      )}
                    </div>
                    <div className="text-sm mt-1 line-clamp-2" style={{ color: "var(--gf-text)" }}>
                      {c.ai_summary || "(AI summary unavailable)"}
                    </div>
                    <div className="text-[10px] mt-1" style={{ color: "var(--gf-text-muted)" }}>
                      Severity: {c.severity} · Confidence: {(c.ai_confidence || 0).toFixed(2)} · {new Date(c.created_at).toLocaleString()}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail */}
          <div className="rounded-2xl border" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }} data-testid="case-detail">
            {!detail ? (
              <div className="p-8 text-sm text-center" style={{ color: "var(--gf-text-muted)" }}>
                Select a case to view details.
              </div>
            ) : (
              <CaseDetail detail={detail} authHeader={authHeader} onDecided={() => { setDetail(null); loadCases(); }} />
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function CaseDetail({ detail, authHeader, onDecided }) {
  const { case: c, reports, business } = detail;
  const [decision, setDecision] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async () => {
    if (!decision) { toast.error("Pick a decision"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/cases/${c.id}/decide`, { decision, notes: notes || undefined }, { headers: authHeader() });
      toast.success(r.data.outcome || "Decision recorded");
      onDecided && onDecided();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Business */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Business</div>
        <div className="mt-1 flex items-center gap-3 flex-wrap">
          <Link to={`/business/${business.id}`} className="font-display text-2xl hover:underline" style={{ color: "var(--gf-text)" }}>
            {business.business_name || business.email}
          </Link>
          <span className="text-[10px] uppercase rounded-full px-2 py-0.5" style={{ background: "var(--gf-surface-2)", color: "var(--gf-text-muted)" }}>
            {business.admin_status || "approved"}
          </span>
          <span className="text-xs" style={{ color: "var(--gf-text-muted)" }}>
            Strikes: <b style={{ color: business.strike_count > 0 ? "#dc2626" : "var(--gf-text)" }}>{business.strike_count || 0}</b>
          </span>
        </div>
      </div>

      {/* AI Summary */}
      <div className="rounded-xl border p-4" style={{ borderColor: "var(--gf-border-strong)", background: "var(--gf-surface-2)" }} data-testid="case-ai-summary">
        <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-accent)" }}>AI triage · Claude Sonnet 4.5</div>
        <p className="text-sm mt-2" style={{ color: "var(--gf-text)" }}>{c.ai_summary || "—"}</p>
        <div className="text-[11px] mt-2 flex gap-4 flex-wrap" style={{ color: "var(--gf-text-muted)" }}>
          <span>Severity: <b style={{ color: "var(--gf-text)" }}>{c.severity}</b></span>
          <span>Confidence: <b style={{ color: "var(--gf-text)" }}>{(c.ai_confidence || 0).toFixed(2)}</b></span>
          <span>Recommended: <b style={{ color: "var(--gf-text)" }}>{c.ai_recommended_action}</b></span>
        </div>
        {c.ai_reasoning && (
          <details className="mt-2 text-[11px]" style={{ color: "var(--gf-text-muted)" }}>
            <summary className="cursor-pointer">Show reasoning</summary>
            <p className="mt-1">{c.ai_reasoning}</p>
          </details>
        )}
      </div>

      {/* Evidence / reports */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Customer report{reports.length !== 1 ? "s" : ""}</div>
        <div className="mt-2 space-y-3">
          {reports.map((r) => (
            <div key={r.id} className="rounded-lg border p-3" style={{ borderColor: "var(--gf-border)" }} data-testid={`case-report-${r.id}`}>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>{r.category}</div>
              <p className="text-sm mt-1 whitespace-pre-wrap" style={{ color: "var(--gf-text)" }}>{r.description}</p>
              {r.booking_snapshot && (
                <div className="text-[11px] mt-2" style={{ color: "var(--gf-text-muted)" }}>
                  Booking: {r.booking_snapshot.service_title} · {r.booking_snapshot.status}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Decision panel */}
      {!c.final_decision && (
        <div className="rounded-xl border p-4" style={{ borderColor: "var(--gf-border-strong)" }} data-testid="case-decision-panel">
          <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Decision</div>
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {DECISIONS.map((d) => (
              <button
                key={d.value}
                data-testid={`case-decision-${d.value}`}
                onClick={() => setDecision(d.value)}
                className="text-sm rounded-lg border px-3 py-2 text-left"
                style={{
                  borderColor: decision === d.value ? d.color : "var(--gf-border-strong)",
                  color: decision === d.value ? "#fff" : "var(--gf-text)",
                  background: decision === d.value ? d.color : "transparent",
                }}>
                {d.label}
              </button>
            ))}
          </div>
          <textarea
            data-testid="case-decision-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes for the audit log..."
            className="mt-3 w-full bg-transparent border rounded-md px-3 py-2 text-sm outline-none"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          />
          <div className="mt-3 flex justify-end">
            <button
              data-testid="case-decide-submit"
              onClick={decide}
              disabled={busy || !decision}
              className="text-sm rounded-full font-medium px-5 py-2 disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              {busy ? "Recording…" : "Record decision"}
            </button>
          </div>
        </div>
      )}

      {/* Audit history */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Audit history</div>
        <div className="mt-2 space-y-2 text-[11px]" style={{ color: "var(--gf-text-soft)" }}>
          {(c.audit || []).map((a, i) => (
            <div key={i} className="flex gap-2">
              <span style={{ color: "var(--gf-text-muted)" }}>{new Date(a.at).toLocaleString()}</span>
              <span><b style={{ color: "var(--gf-text)" }}>{a.actor}</b> · {a.action}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
